import requests from "./requests";
import axios, { AxiosProgressEvent, AxiosPromise, AxiosStatic } from "axios";
import {
  Commitment,
  CommitmentHistoryOutput,
  CommitmentInput,
  CommitmentPendingPayment,
  CommitmentPreviewPayload,
  UpcomingPeriodRate,
  SubmitMandatoryQuestionnaireInput,
  Deal,
  DealsFundraisingCapacity,
  FileInterface,
  GetCommitmentPreviewInput,
  GetCommitmentsInput,
  GetDealsParams,
  GetQuizOutput,
  InvestmentStatementDocInput,
  Investor,
  KycProfile,
  KycDocumentType,
  KycCountryList,
  KycEvidenceUploadArgs,
  KycEvidenceUploadOptions,
  KycIdentityDetailsBody,
  KycRequestAnswerBody,
  KycRequirements,
  KycUploadResult,
  IdvScanStartBody,
  IdvScanStartResponse,
  MandatoryQuestionnaireQuestion,
  NetPromoterScore,
  Payment,
  PaymentInput,
  PaymentTimelineActivityEvent,
  Portfolio,
  PortfolioPerformanceMetrics,
  GetPortfolioPerformanceParams,
  PostQuizInput,
  PostQuizOutput,
  ReturnCalculatorInput,
  ReturnCalculatorResponse,
  Investment,
  EISFundInvestmentResponse,
  EISFundInvestmentSearchRequest,
  Dividend,
  MarketingPreferenceType,
  DocumentEntry,
  SignDocumentOutput,
  CurrencyConversionRatesResponse,
  PortfolioOverviewDocInput,
  AvailablePeriodsQuery,
  AvailablePeriodsResponse,
  ReturnCalculatorV2Response,
  ReturnCalculatorV2Request,
  CurrencyConversionRatesV2Response,
  SupportedCurrencies,
  CurrencyConversionRateBySourceCurrencyResponse,
  CreateZakatPaymentRequest,
  Zakat,
  ZakatResponseDto,
  UpdateZakatRequest,
  UpdateDraftZakatRequest,
  ZakatAsset,
  CreateZakatAssetRequest,
  UpdateZakatAssetRequest,
  ZakatBreakdownResponse,
  ZakatFormulaResponse,
  ZakatLiability,
  CreateZakatLiabilityRequest,
  UpdateZakatLiabilityRequest,
  ZakatPortfolio,
  CreateZakatPaymentResponse,
  PMFInput,
  PMFResponse,
  DealBotQueryResponse,
  DealBotConversationTurn,
  InvestmentUpdate,
  BankVerificationLinkTokenResponseDto,
  CountryISO,
  PlaidCoverageResponseDto,
  PlaidLinkTokenResponseDto,
  VerifyBankAccountRequestDto,
  VerifyBankAccountResponseDto,
  CreateBankAccountFromPlaidRequestDto,
  CreateBankAccountFromPlaidResponseDto,
  GiftAidStatementDocInput,
  WatchlistEntry,
  AddToWatchlistDto,
  IfIsaAccount,
  CreateIfIsaAccountDto,
  UpdateIfIsaAccountDto,
  IfIsaYearlyContribution,
  UpdateInvestorDto,
  IfIsaTransfer,
  CreateIfIsaTransferDto,
  PaymentInitiationLinkTokenResponseDto,
  PaymentInitiationLinkTokenRequestDto,
  UploadIfIsaTransferSignatureDto,
  ProjectReturnsRequest,
  ReturnProjectionResponse,
  Address,
  GooglePlacesAutocompleteItem,
  GooglePlacesAutocompleteRequestDto,
  GooglePlacesReverseGeocodeRequestDto,
  DealInterestStatusResponse,
  ClientEvent,
  GetEventsInput,
  CreateDevReportDto,
  CreateInstallAttributionDto,
  CreateInstallAttributionResponse,
  DevReportResponse,
  EarningsCalendarTimelineResponse,
  UpcomingEarningsResponse,
} from "./types";
import { Entity, EntityDocumentOptions, EntityInput } from "./types/Entity";
import { BankAccount } from "./types/BankAccount";
import {
  MembershipCheckoutSession,
  MembershipCheckoutSessionInput,
} from "./types/Payment";
import {
  AccreditationQuestion,
  AccreditationRequest,
} from "./types/Accreditation";
import { CompleteOnboardingV2Options } from "./types/OnboardingRequests";
import {
  CompleteOnboardingV2Response,
  OnboardInvestorReturn,
  OnboardingRulesProgressResponse,
  OnboardingV2ProfileUpdate,
  OnboardingV2ProgressResponse,
  StepListOnboardingRuleSet,
  UpdateOnboardingAPIPayload,
} from "./types/Onboarding";
import {
  BankAccountDealsResponse,
  CreateUserBankAccountInput,
  PartialUpdateUserBankAccountInput,
  SetDefaultBankAccountInput,
  SetDefaultBankAccountResponse,
  UpdateUserBankAccountInput,
  UserBankAccount,
} from "./types/UserBankAccount";
import {
  NoticePeriodChoiceSubmission,
  FundRestructureChoice,
} from "./requests/noticePeriodChoice";
import {
  IsaWrapCreateInput,
  IsaWrapEligibilityResponse,
  IsaWrapReallocation,
} from "./requests/isaWrap";
import {
  Liquidation,
  LiquidationDestination,
  LiquidationInfo,
  LiquidationInput,
  SetLiquidationDestinationInput,
} from "./types/Liquidation";
import {
  ActivityDistributionDetail,
  CommitmentActivity,
  EarningsDestinations,
} from "./types/CommitmentActivity";
import { Withdrawal } from "./types/Withdrawal";
import { ClientPayment } from "./types/ClientPayment";
import {
  ClientReallocationQuote,
  ClientTransitionQuote,
  ClientTransitionRequest,
  CreateTransitionRequestInput,
} from "./types/ClientTransitionRequest";
import { ClientReallocation } from "./types/ClientReallocation";
import {
  CreateTaxResidencyInput,
  UpdateTaxResidencyInput,
  TaxResidency,
} from "./types/taxResidency";
import { InvestmentUpdateInput } from "./types/InvestmentUpdates";
import { GetGlossaryResponse, GlossaryFilter } from "./types/Glossary";
import {
  ActiveLossEventForMe,
  InvestorLossEventDocument,
  LossEventBuybackChoice,
  SubmitBuybackResponseResult,
} from "./types/loss-events";

type APIParams = {
  serverURL: string;
  appAxios?: AxiosStatic;
  dealAxios?: Pick<AxiosStatic, "get">;
  bankAccountAxios?: Pick<AxiosStatic, "get" | "post" | "put" | "patch">;
};

type InitializeApiSignature = {
  deleteEntityDocument: (
    entityId: string,
    documentId: string
  ) => AxiosPromise<void>;
  deleteKYCDocument: (documentId: string) => AxiosPromise<void>;
  deleteKycDocument: (
    documentId: string
  ) => AxiosPromise<KycRequirements | undefined>;
  getAccreditationQuestionnaire: () => AxiosPromise<AccreditationQuestion[]>;
  getCommitmentHistory: (
    commitmentId: string
  ) => AxiosPromise<CommitmentHistoryOutput>;
  getCommitmentPreview: (
    params: GetCommitmentPreviewInput
  ) => Promise<CommitmentPreviewPayload>;
  getCommitments: (data?: GetCommitmentsInput) => AxiosPromise<Commitment[]>;
  getPendingPayments: () => AxiosPromise<CommitmentPendingPayment[]>;
  getCommitmentUpcomingPeriodRate: (
    commitmentId: string,
    periodId?: string
  ) => AxiosPromise<UpcomingPeriodRate | null>;
  getDeal: (dealId: string) => AxiosPromise<Deal>;
  getDeals: (params?: GetDealsParams) => AxiosPromise<Deal[]>;
  getIfIsaLiveDeals: () => AxiosPromise<Deal[]>;
  getIfIsaTransferDestinations: () => AxiosPromise<Deal[]>;
  getDealsFundraisingCapacity: (
    dealIds: string[]
  ) => AxiosPromise<DealsFundraisingCapacity>;
  getEntities: () => AxiosPromise<Entity[]>;
  getInvestor: () => AxiosPromise<Investor>;
  getPaymentHistories: (
    paymentId: string
  ) => AxiosPromise<PaymentTimelineActivityEvent[]>;
  getPortfolio: () => AxiosPromise<Portfolio[]>;
  getPortfolioPerformance: (
    params?: GetPortfolioPerformanceParams
  ) => AxiosPromise<PortfolioPerformanceMetrics>;
  getQuiz: () => AxiosPromise<GetQuizOutput>;
  postEntity: (entity: EntityInput) => AxiosPromise<Entity>;
  postEntityBankAccount: (
    entityId: string,
    bankAccount: BankAccount
  ) => AxiosPromise<BankAccount>;
  postQuiz: (quiz: PostQuizInput[]) => AxiosPromise<PostQuizOutput>;
  onboardInvestor: (
    data: UpdateOnboardingAPIPayload
  ) => AxiosPromise<OnboardInvestorReturn>;
  getOnboardingV2Progress: () => AxiosPromise<OnboardingV2ProgressResponse>;
  getOnboardingRulesProgress: (
    rules: StepListOnboardingRuleSet
  ) => AxiosPromise<OnboardingRulesProgressResponse>;
  updateOnboardingV2Profile: (
    data: UpdateOnboardingAPIPayload | OnboardingV2ProfileUpdate
  ) => AxiosPromise<Investor>;
  completeOnboardingV2: (
    options?: CompleteOnboardingV2Options
  ) => AxiosPromise<CompleteOnboardingV2Response>;
  getDealInterestStatus: (
    dealId: string
  ) => AxiosPromise<DealInterestStatusResponse>;
  acknowledgeRiskWarning: (data: {
    dealId?: string;
  }) => AxiosPromise<DealInterestStatusResponse>;
  updateDealReminder: (
    dealId: string,
    data: { reminderOptIn: boolean }
  ) => AxiosPromise<DealInterestStatusResponse>;
  reaccreditateInvestor: (
    data: AccreditationRequest
  ) => AxiosPromise<AccreditationQuestion[]>;
  submitOnboardingAccreditation: (
    data: AccreditationRequest
  ) => AxiosPromise<Investor>;
  uploadEntityDocument: (
    entityId: string,
    file: File,
    documentType: EntityDocumentOptions
  ) => AxiosPromise<FileInterface>;
  uploadKYCDocument: (
    file: File,
    documentType: KycDocumentType
  ) => AxiosPromise<FileInterface>;
  postSourceOfFundDescription: (
    sourceOfFundDescription: string
  ) => AxiosPromise<KycProfile>;
  putKycDescriptions: (data: {
    sourceOfFundDescription?: string;
    proofOfAddressDescription?: string;
  }) => AxiosPromise<KycProfile>;
  markKycSubmitted: () => AxiosPromise<
    KycRequirements | KycProfile | undefined
  >;
  getKycRequirements: () => AxiosPromise<KycRequirements>;
  getKycCountryList: () => AxiosPromise<KycCountryList>;
  uploadKycEvidence: (
    file: File,
    args: KycEvidenceUploadArgs,
    options?: KycEvidenceUploadOptions
  ) => AxiosPromise<KycUploadResult>;
  answerKycRequest: (
    requestId: string,
    body: KycRequestAnswerBody
  ) => AxiosPromise<KycRequirements>;
  correctKycIdentityDetails: (
    body: KycIdentityDetailsBody
  ) => AxiosPromise<KycRequirements>;
  postNPS: (npsData: NetPromoterScore) => AxiosPromise<NetPromoterScore>;
  downloadInvestmentStatementDocument: (
    data: InvestmentStatementDocInput
  ) => AxiosPromise;
  downloadInvestmentStatementDocumentV2: (
    data: InvestmentStatementDocInput,
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  ) => AxiosPromise;
  downloadPortfolioOverviewDocument: (
    data: PortfolioOverviewDocInput,
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  ) => AxiosPromise;
  downloadPerformanceOverviewDocument: (
    data: InvestmentStatementDocInput,
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  ) => AxiosPromise;
  membershipCheckoutSession: (
    payload: MembershipCheckoutSessionInput
  ) => AxiosPromise<MembershipCheckoutSession>;
  postCommitment: (commitment: CommitmentInput) => AxiosPromise<Commitment>;
  postPayment: (body: {
    commitmentId: string;
    paymentInput: PaymentInput;
  }) => AxiosPromise<Payment>;
  getReturnCalculator: (
    body: ReturnCalculatorInput
  ) => AxiosPromise<ReturnCalculatorResponse>;
  getReturnCalculatorV2: (
    body: ReturnCalculatorV2Request
  ) => AxiosPromise<ReturnCalculatorV2Response>;
  generateZakatStatement: () => AxiosPromise;
  getUserBankAccounts: () => AxiosPromise<UserBankAccount[]>;
  getDocuments: () => AxiosPromise<{ documents: DocumentEntry[] }>;
  signDocument: (documentId: string) => AxiosPromise<SignDocumentOutput>;
  postUserBankAccount: (
    file: File,
    data: CreateUserBankAccountInput
  ) => AxiosPromise<void>;
  putUserBankAccount: (
    id: string,
    data: UpdateUserBankAccountInput,
    file?: File
  ) => AxiosPromise<void>;
  patchUserBankAccount: (
    id: string,
    data: PartialUpdateUserBankAccountInput
  ) => AxiosPromise<void>;
  deleteUserBankAccount: (id: string) => AxiosPromise<void>;
  setDefaultBankAccount: (
    id: string,
    data: SetDefaultBankAccountInput
  ) => AxiosPromise<SetDefaultBankAccountResponse>;
  getBankAccountDeals: (id: string) => AxiosPromise<BankAccountDealsResponse>;
  getRecommendedBankAccount: (
    commitmentId: string
  ) => AxiosPromise<UserBankAccount | null>;
  findLiquidationById: (liquidationId: string) => AxiosPromise<Liquidation>;
  getLiquidationInfo: (
    dealId: string,
    liquidationId?: string
  ) => AxiosPromise<LiquidationInfo | null>;
  createLiquidation: (liquidation: LiquidationInput) => AxiosPromise<void>;
  getCommitmentActivity: (
    commitmentId: string
  ) => AxiosPromise<CommitmentActivity>;
  getDistributionDetail: (
    commitmentId: string,
    distributionId: string
  ) => AxiosPromise<ActivityDistributionDetail>;
  getEarningsDestinations: (
    commitmentId: string
  ) => AxiosPromise<EarningsDestinations>;
  setEarningsDestination: (
    commitmentId: string,
    destinationDealId: string
  ) => AxiosPromise<EarningsDestinations>;
  getLiquidationDestination: (
    liquidationId: string
  ) => AxiosPromise<LiquidationDestination>;
  setLiquidationDestination: (
    liquidationId: string,
    destination: SetLiquidationDestinationInput
  ) => AxiosPromise<LiquidationDestination>;
  findWithdrawalById: (withdrawalId: string) => AxiosPromise<Withdrawal>;
  findPaymentById: (paymentId: string) => AxiosPromise<ClientPayment>;
  findTransitionRequestById: (
    id: string
  ) => AxiosPromise<ClientTransitionRequest>;
  getTransitionQuote: (
    commitmentId: string
  ) => AxiosPromise<ClientTransitionQuote>;
  getReallocationQuote: (
    commitmentId: string
  ) => AxiosPromise<ClientReallocationQuote>;
  createTransitionRequest: (
    input: CreateTransitionRequestInput
  ) => AxiosPromise<ClientTransitionRequest>;
  cancelTransitionRequest: (
    id: string
  ) => AxiosPromise<ClientTransitionRequest>;
  findReallocationById: (
    reallocationId: string
  ) => AxiosPromise<ClientReallocation>;
  getMandatoryQuestionnaire: () => AxiosPromise<
    MandatoryQuestionnaireQuestion[]
  >;
  submitMandatoryQuestionnaire: (
    data: SubmitMandatoryQuestionnaireInput
  ) => AxiosPromise<void>;
  submitQuickSurvey: (data: { source: string }) => AxiosPromise<void>;
  findInvestmentById: (investmentId: string) => AxiosPromise<Investment>;
  getEISFundInvestments: (
    commitmentId: string,
    params?: EISFundInvestmentSearchRequest
  ) => AxiosPromise<EISFundInvestmentResponse>;
  findDividendById: (dividendId: string) => AxiosPromise<Dividend>;
  postMarketingPreferences: (data: {
    marketingPreference: MarketingPreferenceType;
  }) => AxiosPromise<MarketingPreferenceType>;
  getCurrencyConversionRates: () => AxiosPromise<CurrencyConversionRatesResponse>;
  getCurrencyConversionRatesV2: () => AxiosPromise<CurrencyConversionRatesV2Response>;
  getCurrencyConversionRatesBySourceCurrency: (
    sourceCurrency: SupportedCurrencies
  ) => AxiosPromise<CurrencyConversionRateBySourceCurrencyResponse>;
  createZakatPayment: (
    data: CreateZakatPaymentRequest
  ) => AxiosPromise<CreateZakatPaymentResponse>;
  getZakat: () => AxiosPromise<ZakatResponseDto>;
  getAllZakats: () => AxiosPromise<Zakat[]>;
  getNisab: () => AxiosPromise<number>;
  updateZakat: (data: UpdateZakatRequest) => AxiosPromise<Zakat>;
  createZakatAsset: (data: CreateZakatAssetRequest) => AxiosPromise<ZakatAsset>;
  updateZakatAsset: (
    assetId: string,
    data: UpdateZakatAssetRequest
  ) => AxiosPromise<ZakatAsset>;
  createZakatLiability: (
    data: CreateZakatLiabilityRequest
  ) => AxiosPromise<ZakatLiability>;
  updateZakatLiability: (
    liabilityId: string,
    data: UpdateZakatLiabilityRequest
  ) => AxiosPromise<ZakatLiability>;
  deleteZakatLiability: (liabilityId: string) => AxiosPromise<void>;
  getZakatPortfolio: () => AxiosPromise<ZakatPortfolio[]>;
  getZakatBreakdown: (zakatId: string) => AxiosPromise<ZakatBreakdownResponse>;
  getZakatFormula: () => AxiosPromise<ZakatFormulaResponse>;
  getZakatById: (zakatId: string) => AxiosPromise<Zakat>;
  cloneLatestZakat: () => AxiosPromise<Zakat>;
  getDraftZakat: () => AxiosPromise<Zakat | null>;
  createDraftZakat: () => AxiosPromise<Zakat>;
  updateDraftZakat: (data: UpdateDraftZakatRequest) => AxiosPromise<Zakat>;
  deleteZakatAsset: (assetId: string) => AxiosPromise<void>;
  postPMF: (pmfData: PMFInput) => AxiosPromise<PMFResponse>;
  downloadGiftAidStatementDocument: (
    data: GiftAidStatementDocInput,
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  ) => AxiosPromise;
  queryDealBot: (
    dealId: string,
    message: string,
    history?: DealBotConversationTurn[],
    conversationSessionId?: string,
    conversationId?: string
  ) => AxiosPromise<DealBotQueryResponse>;
  createTaxResidency: (
    data: CreateTaxResidencyInput
  ) => AxiosPromise<TaxResidency>;
  updateTaxResidency: (
    taxResidencyId: string,
    data: UpdateTaxResidencyInput
  ) => AxiosPromise<TaxResidency>;
  deleteTaxResidency: (taxResidencyId: string) => AxiosPromise<void>;
  findInvestmentUpdates: (
    data?: InvestmentUpdateInput
  ) => AxiosPromise<InvestmentUpdate[]>;
  markInvestmentUpdateAsViewed: (
    investmentUpdateId: string
  ) => AxiosPromise<void>;
  checkPlaidCoverage: (
    country: CountryISO
  ) => AxiosPromise<PlaidCoverageResponseDto>;
  getBankVerificationLinkToken: (params?: {
    platform?: "iOS" | "Android";
    androidPackageName?: string;
  }) => AxiosPromise<BankVerificationLinkTokenResponseDto>;
  verifyBankAccount: (
    data: VerifyBankAccountRequestDto
  ) => AxiosPromise<VerifyBankAccountResponseDto>;
  createBankAccountFromPlaid: (
    data: CreateBankAccountFromPlaidRequestDto
  ) => AxiosPromise<CreateBankAccountFromPlaidResponseDto>;
  getWatchlist: () => AxiosPromise<WatchlistEntry[]>;
  addToWatchlist: (data: AddToWatchlistDto) => AxiosPromise<WatchlistEntry>;
  removeFromWatchlist: (dealId: string) => AxiosPromise<void>;
  getIfIsaAccount: () => AxiosPromise<IfIsaAccount | null>;
  createIfIsaAccount: (
    data: CreateIfIsaAccountDto
  ) => AxiosPromise<IfIsaAccount>;
  updateIfIsaAccount: (
    id: string,
    data: UpdateIfIsaAccountDto
  ) => AxiosPromise<IfIsaAccount>;
  getYearlyContributionForCurrentYear: () => AxiosPromise<IfIsaYearlyContribution>;
  updateInvestor: (data: UpdateInvestorDto) => AxiosPromise<Investor>;
  createIfIsaTransfer: (
    data: CreateIfIsaTransferDto
  ) => AxiosPromise<IfIsaTransfer>;
  uploadIfIsaTransferSignature: (
    data: UploadIfIsaTransferSignatureDto
  ) => AxiosPromise<IfIsaTransfer>;
  getMyIfIsaTransfers: () => AxiosPromise<IfIsaTransfer[]>;
  getPaymentInitiationLinkToken: (
    data: PaymentInitiationLinkTokenRequestDto
  ) => AxiosPromise<PaymentInitiationLinkTokenResponseDto>;
  getIdvLinkToken: () => AxiosPromise<PlaidLinkTokenResponseDto>;
  startIdvScan: (body: IdvScanStartBody) => AxiosPromise<IdvScanStartResponse>;
  acknowledgePayment: (paymentId: string) => AxiosPromise<Payment>;
  cancelPayment: (paymentId: string) => AxiosPromise<Payment>;
  getProjectReturns: (
    body: ProjectReturnsRequest
  ) => AxiosPromise<ReturnProjectionResponse>;
  postGooglePlacesAutocomplete: (
    data: GooglePlacesAutocompleteRequestDto
  ) => AxiosPromise<GooglePlacesAutocompleteItem[]>;
  getGooglePlacesDetails: (placeId: string) => AxiosPromise<Address>;
  postGooglePlacesReverseGeocode: (
    data: GooglePlacesReverseGeocodeRequestDto
  ) => AxiosPromise<Address>;
  getGlossary: (filters?: GlossaryFilter) => AxiosPromise<GetGlossaryResponse>;
  getEvents: (data?: GetEventsInput) => AxiosPromise<ClientEvent[]>;
  getEventById: (eventId: string) => AxiosPromise<ClientEvent>;
  createDevReport: (
    data: CreateDevReportDto
  ) => AxiosPromise<DevReportResponse>;
  createInstallAttribution: (
    data: CreateInstallAttributionDto
  ) => AxiosPromise<CreateInstallAttributionResponse>;
  getEarningsCalendarTimeline: (
    dealId: string,
    commitmentId?: string
  ) => AxiosPromise<EarningsCalendarTimelineResponse>;
  getUpcomingEarnings: () => AxiosPromise<UpcomingEarningsResponse>;
  submitNoticePeriodChoice: (
    data: NoticePeriodChoiceSubmission
  ) => AxiosPromise<{ reallocationIds: string[] }>;
  getNoticePeriodChoices: (
    dealId: string
  ) => AxiosPromise<FundRestructureChoice[]>;
  getIsaWrapEligibility: () => AxiosPromise<IsaWrapEligibilityResponse>;
  submitIsaWrap: (
    data: IsaWrapCreateInput
  ) => AxiosPromise<IsaWrapReallocation>;
  getPortfolioStatementAvailablePeriods: (
    query: AvailablePeriodsQuery
  ) => AxiosPromise<AvailablePeriodsResponse>;
  getActiveLossEventsForMe: () => AxiosPromise<ActiveLossEventForMe[]>;
  submitLossEventBuybackResponse: (
    lossEventId: string,
    data: { choice: LossEventBuybackChoice; consentConfirmed?: boolean }
  ) => AxiosPromise<SubmitBuybackResponseResult>;
  getLossEventSpaPreview: (lossEventId: string) => AxiosPromise<Blob>;
  getLossEventDocuments: (
    lossEventId: string
  ) => AxiosPromise<InvestorLossEventDocument[]>;
};

const InitializeApi: (payload: APIParams) => InitializeApiSignature = ({
  serverURL,
  appAxios = axios,
  dealAxios = appAxios,
  bankAccountAxios = appAxios,
}) => {
  return {
    deleteEntityDocument: requests.deleteEntityDocument(serverURL, appAxios),
    getCommitmentHistory: requests.getCommitmentHistory(serverURL, appAxios),
    deleteKYCDocument: requests.deleteKYCDocument(serverURL, appAxios),
    deleteKycDocument: requests.deleteKycDocument(serverURL, appAxios),
    getAccreditationQuestionnaire: requests.getAccreditationQuestionnaire(
      serverURL,
      appAxios
    ),
    getCommitmentPreview: requests.getCommitmentPreview(serverURL, appAxios),
    getDeal: requests.getDeal(serverURL, dealAxios),
    getDeals: requests.getDeals(serverURL, appAxios),
    getIfIsaLiveDeals: requests.getIfIsaLiveDeals(serverURL, appAxios),
    getIfIsaTransferDestinations: requests.getIfIsaTransferDestinations(
      serverURL,
      appAxios
    ),
    getDealsFundraisingCapacity: requests.getDealsFundraisingCapacity(
      serverURL,
      appAxios
    ),
    getEntities: requests.getEntities(serverURL, appAxios),
    getInvestor: requests.getInvestor(serverURL, appAxios),
    getPaymentHistories: requests.getPaymentHistories(serverURL, appAxios),
    getPortfolio: requests.getPortfolio(serverURL, appAxios),
    getPortfolioPerformance: requests.getPortfolioPerformance(
      serverURL,
      appAxios
    ),
    getQuiz: requests.getQuiz(serverURL, appAxios),
    postCommitment: requests.postCommitment(serverURL, appAxios),
    postPayment: requests.postPayment(serverURL, appAxios),
    postEntity: requests.postEntity(serverURL, appAxios),
    postEntityBankAccount: requests.postEntityBankAccount(serverURL, appAxios),
    postQuiz: requests.postQuiz(serverURL, appAxios),
    onboardInvestor: requests.onboardInvestor(serverURL, appAxios),
    getOnboardingV2Progress: requests.getOnboardingV2Progress(
      serverURL,
      appAxios
    ),
    getOnboardingRulesProgress: requests.getOnboardingRulesProgress(
      serverURL,
      appAxios
    ),
    updateOnboardingV2Profile: requests.updateOnboardingV2Profile(
      serverURL,
      appAxios
    ),
    completeOnboardingV2: requests.completeOnboardingV2(serverURL, appAxios),
    getDealInterestStatus: requests.getDealInterestStatus(serverURL, appAxios),
    acknowledgeRiskWarning: requests.acknowledgeRiskWarning(
      serverURL,
      appAxios
    ),
    updateDealReminder: requests.updateDealReminder(serverURL, appAxios),
    reaccreditateInvestor: requests.reaccreditateInvestor(serverURL, appAxios),
    submitOnboardingAccreditation: requests.submitOnboardingAccreditation(
      serverURL,
      appAxios
    ),
    uploadEntityDocument: requests.uploadEntityDocument(serverURL, appAxios),
    uploadKYCDocument: requests.uploadKYCDocument(serverURL, appAxios),
    postSourceOfFundDescription: requests.postSourceOfFundDescription(
      serverURL,
      appAxios
    ),
    putKycDescriptions: requests.putKycDescriptions(serverURL, appAxios),
    markKycSubmitted: requests.markKycSubmitted(serverURL, appAxios),
    getKycRequirements: requests.getKycRequirements(serverURL, appAxios),
    getKycCountryList: requests.getKycCountryList(serverURL, appAxios),
    uploadKycEvidence: requests.uploadKycEvidence(serverURL, appAxios),
    answerKycRequest: requests.answerKycRequest(serverURL, appAxios),
    correctKycIdentityDetails: requests.correctKycIdentityDetails(
      serverURL,
      appAxios
    ),
    postNPS: requests.postNPS(serverURL, appAxios),
    downloadInvestmentStatementDocument:
      requests.downloadInvestmentStatementDocument(serverURL, appAxios),
    downloadInvestmentStatementDocumentV2:
      requests.downloadInvestmentStatementDocumentV2(serverURL, appAxios),
    downloadPortfolioOverviewDocument:
      requests.downloadPortfolioOverviewDocument(serverURL, appAxios),
    downloadPerformanceOverviewDocument:
      requests.downloadPerformanceOverviewDocument(serverURL, appAxios),
    membershipCheckoutSession: requests.membershipCheckoutSession(
      serverURL,
      appAxios
    ),
    getCommitments: requests.getCommitments(serverURL, appAxios),
    getPendingPayments: requests.getPendingPayments(serverURL, appAxios),
    getCommitmentUpcomingPeriodRate: requests.getCommitmentUpcomingPeriodRate(
      serverURL,
      appAxios
    ),
    getReturnCalculator: requests.getReturnCalculator(serverURL, appAxios),
    getReturnCalculatorV2: requests.getReturnCalculatorV2(serverURL, appAxios),
    generateZakatStatement: requests.generateZakatStatement(
      serverURL,
      appAxios
    ),
    getDocuments: requests.getDocuments(serverURL, appAxios),
    signDocument: requests.signDocument(serverURL, appAxios),
    getUserBankAccounts: requests.getUserBankAccounts(
      serverURL,
      bankAccountAxios
    ),
    postUserBankAccount: requests.postUserBankAccount(
      serverURL,
      bankAccountAxios
    ),
    putUserBankAccount: requests.putUserBankAccount(
      serverURL,
      bankAccountAxios
    ),
    patchUserBankAccount: requests.patchUserBankAccount(
      serverURL,
      bankAccountAxios
    ),
    deleteUserBankAccount: requests.deleteUserBankAccount(serverURL, appAxios),
    setDefaultBankAccount: requests.setDefaultBankAccount(
      serverURL,
      bankAccountAxios
    ),
    getBankAccountDeals: requests.getBankAccountDeals(
      serverURL,
      bankAccountAxios
    ),
    getRecommendedBankAccount: requests.getRecommendedBankAccount(
      serverURL,
      appAxios
    ),
    findLiquidationById: requests.findLiquidationById(serverURL, appAxios),
    getLiquidationInfo: requests.getLiquidationInfo(serverURL, appAxios),
    getCommitmentActivity: requests.getCommitmentActivity(serverURL, appAxios),
    getDistributionDetail: requests.getDistributionDetail(serverURL, appAxios),
    getEarningsDestinations: requests.getEarningsDestinations(
      serverURL,
      appAxios
    ),
    setEarningsDestination: requests.setEarningsDestination(
      serverURL,
      appAxios
    ),
    getLiquidationDestination: requests.getLiquidationDestination(
      serverURL,
      appAxios
    ),
    setLiquidationDestination: requests.setLiquidationDestination(
      serverURL,
      appAxios
    ),
    createLiquidation: requests.createLiquidation(serverURL, appAxios),
    findWithdrawalById: requests.findWithdrawalById(serverURL, appAxios),
    findPaymentById: requests.findPaymentById(serverURL, appAxios),
    findTransitionRequestById: requests.findTransitionRequestById(
      serverURL,
      appAxios
    ),
    getTransitionQuote: requests.getTransitionQuote(serverURL, appAxios),
    getReallocationQuote: requests.getReallocationQuote(serverURL, appAxios),
    createTransitionRequest: requests.createTransitionRequest(
      serverURL,
      appAxios
    ),
    cancelTransitionRequest: requests.cancelTransitionRequest(
      serverURL,
      appAxios
    ),
    findReallocationById: requests.findReallocationById(serverURL, appAxios),
    getMandatoryQuestionnaire: requests.getMandatoryQuestionnaire(
      serverURL,
      appAxios
    ),
    submitMandatoryQuestionnaire: requests.submitMandatoryQuestionnaire(
      serverURL,
      appAxios
    ),
    submitQuickSurvey: requests.submitQuickSurvey(serverURL, appAxios),
    findInvestmentById: requests.findInvestmentById(serverURL, appAxios),
    getEISFundInvestments: requests.getEISFundInvestments(serverURL, appAxios),
    findDividendById: requests.findDividendById(serverURL, appAxios),
    postMarketingPreferences: requests.postMarketingPreferences(
      serverURL,
      appAxios
    ),
    getCurrencyConversionRates: requests.getCurrencyConversionRates(
      serverURL,
      appAxios
    ),
    getCurrencyConversionRatesV2: requests.getCurrencyConversionRatesV2(
      serverURL,
      appAxios
    ),
    getCurrencyConversionRatesBySourceCurrency:
      requests.getCurrencyConversionRatesBySourceCurrency(serverURL, appAxios),
    getZakat: requests.getZakat(serverURL, appAxios),
    getAllZakats: requests.getAllZakats(serverURL, appAxios),
    getNisab: requests.getNisab(serverURL, appAxios),
    updateZakat: requests.updateZakat(serverURL, appAxios),
    createZakatAsset: requests.createZakatAsset(serverURL, appAxios),
    updateZakatAsset: requests.updateZakatAsset(serverURL, appAxios),
    createZakatLiability: requests.createZakatLiability(serverURL, appAxios),
    updateZakatLiability: requests.updateZakatLiability(serverURL, appAxios),
    deleteZakatAsset: requests.deleteZakatAsset(serverURL, appAxios),
    deleteZakatLiability: requests.deleteZakatLiability(serverURL, appAxios),
    getZakatPortfolio: requests.getZakatPortfolio(serverURL, appAxios),
    getZakatBreakdown: requests.getZakatBreakdown(serverURL, appAxios),
    getZakatFormula: requests.getZakatFormula(serverURL, appAxios),
    getZakatById: requests.getZakatById(serverURL, appAxios),
    cloneLatestZakat: requests.cloneLatestZakat(serverURL, appAxios),
    getDraftZakat: requests.getDraftZakat(serverURL, appAxios),
    createDraftZakat: requests.createDraftZakat(serverURL, appAxios),
    updateDraftZakat: requests.updateDraftZakat(serverURL, appAxios),
    createZakatPayment: requests.createZakatPayment(serverURL, appAxios),
    postPMF: requests.postPMF(serverURL, appAxios),
    downloadGiftAidStatementDocument: requests.downloadGiftAidStatementDocument(
      serverURL,
      appAxios
    ),
    queryDealBot: requests.queryDealBot(serverURL, appAxios),
    createTaxResidency: requests.createTaxResidency(serverURL, appAxios),
    updateTaxResidency: requests.updateTaxResidency(serverURL, appAxios),
    deleteTaxResidency: requests.deleteTaxResidency(serverURL, appAxios),
    findInvestmentUpdates: requests.findInvestmentUpdates(serverURL, appAxios),
    markInvestmentUpdateAsViewed: requests.markInvestmentUpdateAsViewed(
      serverURL,
      appAxios
    ),
    checkPlaidCoverage: requests.checkPlaidCoverage(serverURL, appAxios),
    getBankVerificationLinkToken: requests.getBankVerificationLinkToken(
      serverURL,
      appAxios
    ),
    verifyBankAccount: requests.verifyBankAccount(serverURL, appAxios),
    createBankAccountFromPlaid: requests.createBankAccountFromPlaid(
      serverURL,
      bankAccountAxios
    ),
    getWatchlist: requests.getWatchlist(serverURL, appAxios),
    addToWatchlist: requests.addToWatchlist(serverURL, appAxios),
    removeFromWatchlist: requests.removeFromWatchlist(serverURL, appAxios),
    getIfIsaAccount: requests.getIfIsaAccount(serverURL, appAxios),
    createIfIsaAccount: requests.createIfIsaAccount(serverURL, appAxios),
    updateIfIsaAccount: requests.updateIfIsaAccount(serverURL, appAxios),
    getYearlyContributionForCurrentYear:
      requests.getYearlyContributionForCurrentYear(serverURL, appAxios),
    updateInvestor: requests.updateInvestor(serverURL, appAxios),
    createIfIsaTransfer: requests.createIfIsaTransfer(serverURL, appAxios),
    uploadIfIsaTransferSignature: requests.uploadIfIsaTransferSignature(
      serverURL,
      appAxios
    ),
    getMyIfIsaTransfers: requests.getMyIfIsaTransfers(serverURL, appAxios),
    getPaymentInitiationLinkToken: requests.getPaymentInitiationLinkToken(
      serverURL,
      appAxios
    ),
    getIdvLinkToken: requests.getIdvLinkToken(serverURL, appAxios),
    startIdvScan: requests.startIdvScan(serverURL, appAxios),
    acknowledgePayment: requests.acknowledgePayment(serverURL, appAxios),
    cancelPayment: requests.cancelPayment(serverURL, appAxios),
    getProjectReturns: requests.getProjectReturns(serverURL, appAxios),
    postGooglePlacesAutocomplete: requests.postGooglePlacesAutocomplete(
      serverURL,
      appAxios
    ),
    getGooglePlacesDetails: requests.getGooglePlacesDetails(
      serverURL,
      appAxios
    ),
    postGooglePlacesReverseGeocode: requests.postGooglePlacesReverseGeocode(
      serverURL,
      appAxios
    ),
    getGlossary: requests.getGlossary(serverURL, appAxios),
    getEvents: requests.getEvents(serverURL, appAxios),
    getEventById: requests.getEventById(serverURL, appAxios),
    createDevReport: requests.createDevReport(serverURL, appAxios),
    createInstallAttribution: requests.createInstallAttribution(
      serverURL,
      appAxios
    ),
    getEarningsCalendarTimeline: requests.getEarningsCalendarTimeline(
      serverURL,
      appAxios
    ),
    getUpcomingEarnings: requests.getUpcomingEarnings(serverURL, appAxios),
    submitNoticePeriodChoice: requests.submitNoticePeriodChoice(
      serverURL,
      appAxios
    ),
    getNoticePeriodChoices: requests.getNoticePeriodChoices(
      serverURL,
      appAxios
    ),
    getIsaWrapEligibility: requests.getIsaWrapEligibility(serverURL, appAxios),
    submitIsaWrap: requests.submitIsaWrap(serverURL, appAxios),
    getPortfolioStatementAvailablePeriods:
      requests.getPortfolioStatementAvailablePeriods(serverURL, appAxios),
    getActiveLossEventsForMe: requests.getActiveLossEventsForMe(
      serverURL,
      appAxios
    ),
    submitLossEventBuybackResponse: requests.submitLossEventBuybackResponse(
      serverURL,
      appAxios
    ),
    getLossEventSpaPreview: requests.getLossEventSpaPreview(
      serverURL,
      appAxios
    ),
    getLossEventDocuments: requests.getLossEventDocuments(serverURL, appAxios),
  };
};

export * from "./types";
export * from "./helpers";
export type {
  NoticePeriodAllocation,
  EntitySubmission,
  NoticePeriodChoiceSubmission,
  FundRestructureAllocationData,
  FundRestructureSubmissionData,
  FundRestructureChoice,
} from "./requests/noticePeriodChoice";
export type {
  IsaWrapCurrency,
  IsaWrapNoticePeriodOption,
  IsaWrapPersonalCommitment,
  IsaWrapEligibleDeal,
  IsaWrapEligibilityResponse,
  IsaWrapCreateInput,
  IsaWrapReallocationStatus,
  IsaWrapReallocationCommitment,
  IsaWrapReallocationNote,
  IsaWrapReallocation,
} from "./requests/isaWrap";
export { InitializeApi };
